"use client"

import * as React from "react";

import { RequiredMarker } from "@/components/required-fields-legend";
import { type InputFieldVariantProps } from "@/components/ui/field-variants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fieldDescribedByIds } from "@/lib/field-described-by";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export type CustomInputProps = Omit<React.ComponentProps<"input">, keyof InputFieldVariantProps> &
  InputFieldVariantProps & {
    /** Visible label text paired with the control (also used for the `Label`) */
    label: React.ReactNode
    optional?: boolean
    /** When set, appends a visual asterisk (mutually exclusive with `optional` in typical use). */
    required?: boolean
    /**
     * Validation copy for the reserved error row. When omitted, the row still occupies space so
     * showing a message later does not shift layout.
     */
    errorMessage?: string
    /** `id` on the error paragraph (referenced by `aria-describedby` when `errorMessage` is set) */
    errorId: string
    /**
     * Screen-reader-only hint when it adds information beyond the visible `label` (e.g. format example).
     * Omit when the label (or error message) is enough — hints are not combined with errors in `aria-describedby`.
     */
    hintId?: string
    hintText?: string
    /** Optional classes for the error row height (defaults reserve roughly one line of `body-copy-s`) */
    errorReserveClassName?: string
    className?: string
    labelClassName?: string
    /** Classes merged onto the inner `Input` (e.g. width, border) */
    inputClassName?: string
    /** Classes merged onto the outer `div` (e.g. padding) */
    paddingClassName?: string
    /** Optional control rendered inside the input row (e.g. submit button), vertically centered on the field */
    endAdornment?: React.ReactNode
    /** Classes merged onto the end-adornment wrapper (position defaults to `absolute top-1/2 right-2 -translate-y-1/2`) */
    endAdornmentClassName?: string
  }

/**
 * Composed single-line field: `Input` + `Label`, a screen-reader-only hint, and a validation line.
 *
 * **Layout:** The error area always keeps a fixed footprint (non-breaking space when empty, transparent
 * text when there is no error) so toggling `errorMessage` does not jump the page—useful for dense forms
 * such as newsletter or CRM blocks.
 *
 * **Variants:** Pass `variant` from the shared field system: **`floating`** (floating label; default for
 * this component) or **`default`** (stacked label). All other native `input` props are forwarded
 * (`type`, `disabled`, `autoComplete`, react-hook-form `register` spread, etc.).
 *
 * **Typical props:** `label`, `id`, `hintId` + `hintText`, `errorId` + optional `errorMessage`, plus
 * `className` / `labelClassName` / `inputClassName` for layout and theming.
 */
const CustomInput = React.forwardRef<HTMLInputElement, CustomInputProps>(function CustomInput(
  {
    label,
    optional,
    required,
    errorMessage,
    errorId,
    hintId,
    hintText,
    errorReserveClassName,
    className,
    labelClassName,
    inputClassName,
    variant = "floating",
    id,
    "aria-describedby": ariaDescribedByProp,
    paddingClassName = "pb-4",
    endAdornment,
    endAdornmentClassName,
    ...inputProps
  },
  ref
) {
  const { t } = useTranslation('common');
  const hasError = Boolean(errorMessage)
  const hintContent = hintText?.trim() ?? ""
  const hasHint = hintContent.length > 0
  const resolvedHintId = hasHint ? (hintId ?? (id ? `${id}-hint` : undefined)) : undefined
  const describedBy = fieldDescribedByIds({
    errorId,
    hasError,
    hintId: resolvedHintId,
  })

  return (
    <div className={cn("flex w-full min-w-0 flex-col mb-2", className)}>
      {/* Outer: reserves space for the error line without shifting the floating label center */}
      <div className={cn("relative", paddingClassName)}>
        <div className="relative">
          <Input
            ref={ref}
            id={id}
            variant={variant}
            required={required}
            {...inputProps}
            aria-invalid={hasError}
            aria-describedby={ariaDescribedByProp ?? describedBy}
            className={cn(endAdornment != null && "pe-28", inputClassName)}
          />
          <Label htmlFor={id} variant={variant} data-error={hasError} className={labelClassName}>
            {label}
            {required && !optional ? <RequiredMarker /> : null}
            {optional ? <span className="text-muted-foreground">{t('optionalFieldLabel')}</span> : null}
          </Label>
          {hasHint && resolvedHintId ? (
            <span id={resolvedHintId} className="sr-only">
              {hintContent}
            </span>
          ) : null}
          {endAdornment != null ? (
            <div
              data-slot="end-adornment"
              className={cn(
                "absolute top-1/2 right-2 z-20 -translate-y-1/2",
                endAdornmentClassName
              )}
            >
              {endAdornment}
            </div>
          ) : null}
        </div>
        {hasError ? (
          <p
            id={errorId}
            className={cn("body-copy-s text-destructive absolute bottom-0 left-0 right-0 whitespace-nowrap", errorReserveClassName)}
          >
            {errorMessage}
          </p>
        ) : null}
      </div>
    </div>
  )
})

export { CustomInput }
